/**
 * Product utility functions
 * Handles slug generation, URL building, and product-related helpers
 */

/**
 * Affiliate identifiers for URL slugs
 * When adding new affiliates, add their short code here
 */
export const AFFILIATE_SLUGS = {
  amazon: 'amzn',
  // Future affiliates:
  // iherb: 'ihrb',
  // vitacost: 'vcst',
};

/**
 * Generate a URL-friendly slug from a product
 * Uses static affiliate identifier for clean, predictable URLs
 * @param {Object} product - Product object with brand and product_name
 * @param {string} affiliate - Affiliate source (default: 'amazon')
 * @returns {string} URL-safe slug
 */
export const generateProductSlug = (product, affiliate = 'amazon') => {
  if (!product) return '';
  
  const brand = product.brand?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || '';
  const name = product.product_name?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50) || '';
  
  const slug = `${brand}-${name}`.replace(/-+/g, '-').replace(/^-|-$/g, '');
  
  // Use static affiliate identifier (e.g., 'amzn' for Amazon)
  const affiliateSuffix = AFFILIATE_SLUGS[affiliate] || affiliate.slice(0, 4);
  
  return `${slug}-${affiliateSuffix}`;
};

/**
 * Generate the full product page URL
 * @param {Object} product - Product object
 * @returns {string} Full relative URL path
 */
export const getProductUrl = (product) => {
  if (!product) return '/market';
  
  const supplementKey = product.supplement_key || product.supplementKey || '';
  const slug = generateProductSlug(product);
  
  return `/market/${supplementKey}/${slug}`;
};

/**
 * Find a product by its slug from a list of products
 * @param {Array} products - Array of product objects
 * @param {string} slug - The slug to match
 * @returns {Object|null} Matching product or null
 */
export const findProductBySlug = (products, slug) => {
  if (!products || !slug) return null;
  
  return products.find(product => generateProductSlug(product) === slug) || null;
};

/**
 * Format price for display
 * @param {number|string} price - Price value
 * @param {boolean} showDisclaimer - Whether to indicate price may vary
 * @returns {Object} Formatted price object
 */
export const formatPrice = (price) => {
  if (!price || isNaN(parseFloat(price))) {
    return {
      display: 'See on Amazon',
      value: null,
      hasPrice: false,
    };
  }
  
  const numPrice = parseFloat(price);
  return {
    display: `$${numPrice.toFixed(2)}`,
    value: numPrice,
    hasPrice: true,
  };
};

/**
 * Format cost per serving
 * @param {number|string} costPerServing - Cost per serving value
 * @returns {string} Formatted string
 */
export const formatCostPerServing = (costPerServing) => {
  if (!costPerServing || isNaN(parseFloat(costPerServing))) {
    return null;
  }
  
  return `$${parseFloat(costPerServing).toFixed(2)}/serving`;
};

/**
 * Get unit text based on form factor
 * @param {string} formFactor - Product form factor
 * @param {number} count - Quantity
 * @returns {string} Appropriate unit text
 */
export const getUnitText = (formFactor, count) => {
  const quantity = parseInt(count) || 1;
  
  if (!formFactor) return quantity === 1 ? 'unit' : 'units';
  
  const lowerForm = formFactor.toLowerCase();
  
  // Handle countable items
  if (lowerForm.includes('capsule') || lowerForm.includes('tablet') || 
      lowerForm.includes('gummy') || lowerForm.includes('pill') || 
      lowerForm.includes('softgel') || lowerForm.includes('gelcap')) {
    return quantity === 1 
      ? lowerForm.replace(/s$/, '')
      : lowerForm;
  }
  
  // Handle powder
  if (lowerForm.includes('powder')) {
    return quantity === 1 ? 'scoop' : 'scoops';
  }
  
  // Handle liquid
  if (lowerForm.includes('liquid') || lowerForm.includes('oil') || 
      lowerForm.includes('tincture') || lowerForm.includes('solution')) {
    return 'ml';
  }
  
  return formFactor;
};

/**
 * Format supplement key for display
 * @param {string} key - Supplement key (e.g., 'vitamin_d3')
 * @returns {string} Display name (e.g., 'Vitamin D3')
 */
export const formatSupplementName = (key) => {
  if (!key) return '';
  
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generate meta description for product page
 * @param {Object} product - Product object
 * @returns {string} SEO meta description
 */
export const generateProductDescription = (product) => {
  if (!product) return '';
  
  const parts = [];
  
  // Product name and brand
  parts.push(`${product.product_name} by ${product.brand}.`);
  
  // Dosage info
  if (product.dosage_amount && product.dosage_unit) {
    parts.push(`${product.dosage_amount}${product.dosage_unit} per serving.`);
  }
  
  // Certifications
  const certs = [];
  if (product.third_party_tested) certs.push('Third-party tested');
  if (product.gmp_certified_facility) certs.push('GMP certified');
  if (Array.isArray(product.testing_3rd_party) && product.testing_3rd_party.length > 0) {
    certs.push(...product.testing_3rd_party.slice(0, 2));
  }
  
  if (certs.length > 0) {
    parts.push(certs.join(', ') + '.');
  }
  
  // Call to action
  parts.push('Shop with confidence at myStack.');
  
  return parts.join(' ').substring(0, 160);
};

/**
 * Get all unique certifications from a product
 * @param {Object} product - Product object
 * @returns {Array} Deduplicated certification list
 */
export const getProductCertifications = (product) => {
  if (!product) return [];
  
  const certifications = [
    ...(Array.isArray(product.testing_3rd_party) ? product.testing_3rd_party : []),
    ...(Array.isArray(product.other_certifications) ? product.other_certifications : []),
  ].filter(Boolean);
  
  // Deduplicate based on lowercase comparison
  const seen = new Set();
  return certifications.filter(cert => {
    const lower = cert.toLowerCase();
    if (seen.has(lower)) return false;
    seen.add(lower);
    return true;
  });
};

/**
 * Get dietary attributes that are true for a product
 * @param {Object} product - Product object
 * @returns {Array} List of dietary attribute objects
 */
export const getDietaryAttributes = (product) => {
  if (!product) return [];
  
  const attributes = [];
  
  if (product.vegetarian) attributes.push({ key: 'vegetarian', label: 'Vegetarian' });
  if (product.non_gmo) attributes.push({ key: 'non_gmo', label: 'Non-GMO' });
  if (product.gluten_free) attributes.push({ key: 'gluten_free', label: 'Gluten-Free' });
  if (product.soy_free) attributes.push({ key: 'soy_free', label: 'Soy-Free' });
  if (product.no_artificial_colors) attributes.push({ key: 'no_artificial_colors', label: 'No Artificial Colors' });
  if (product.no_artificial_flavors) attributes.push({ key: 'no_artificial_flavors', label: 'No Artificial Flavors' });
  if (product.no_artificial_preservatives) attributes.push({ key: 'no_artificial_preservatives', label: 'No Artificial Preservatives' });
  
  return attributes;
};

export default {
  generateProductSlug,
  getProductUrl,
  findProductBySlug,
  formatPrice,
  formatCostPerServing,
  getUnitText,
  formatSupplementName,
  generateProductDescription,
  getProductCertifications,
  getDietaryAttributes,
};

